"use client";

import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { getBrandingConfig, DEFAULT_BRANDING } from '@/services/BrandingService';
import Link from 'next/link';
import Image from 'next/image';
import { ChevronDownIcon, MenuIcon } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import LanguageSelector from './LanguageSelector';
import { languageConfig, convertLegacyUrl } from '@/lib/i18n-config';

// Define language-specific home paths for root landing pages
const homePaths = {
  en: '/engelberg-accommodation',
  de: '/de/engelberg-ferienwohnung',
  es: '/es/alojamiento-engelberg',
  fr: '/fr/hebergement-engelberg'
};

// Define language-specific paths for localized sections
const contactPaths = {
  en: {
    base: '/engelberg-accommodation/contact',
    directions: '/engelberg-accommodation/contact/directions',
    inquiry: '/engelberg-accommodation/contact/inquiry'
  },
  de: {
    base: '/de/engelberg-ferienwohnung/kontakt',
    directions: '/de/engelberg-ferienwohnung/kontakt/anfahrt',
    inquiry: '/de/engelberg-ferienwohnung/kontakt/anfrage'
  },
  es: {
    base: '/es/alojamiento-engelberg/contacto',
    directions: '/es/alojamiento-engelberg/contacto/como-llegar',
    inquiry: '/es/alojamiento-engelberg/contacto/consulta'
  },
  fr: {
    base: '/fr/hebergement-engelberg/contact',
    directions: '/fr/hebergement-engelberg/contact/itineraire',
    inquiry: '/fr/hebergement-engelberg/contact/demande'
  }
};

// Define language-specific paths for reviews
const reviewsPaths = {
  en: '/engelberg-accommodation/reviews',
  de: '/de/engelberg-ferienwohnung/bewertungen',
  es: '/es/alojamiento-engelberg/opiniones',
  fr: '/fr/hebergement-engelberg/avis'
};

// Define language-specific paths for sustainable accommodation
const sustainablePaths = {
  en: '/engelberg-accommodation/sustainable-accommodation',
  de: '/de/engelberg-ferienwohnung/nachhaltige-unterkunft',
  es: '/es/alojamiento-engelberg/alojamiento-sostenible',
  fr: '/fr/hebergement-engelberg/hebergement-durable'
};

// Define language-specific paths for booking section
const bookingPaths = {
  base: {
    en: '/engelberg-accommodation/booking',
    de: '/de/engelberg-ferienwohnung/buchung',
    es: '/es/alojamiento-engelberg/reserva',
    fr: '/fr/hebergement-engelberg/reservation'
  },
  availability: {
    en: '/engelberg-accommodation/booking/availability',
    de: '/de/engelberg-ferienwohnung/verfugbarkeit',
    es: '/es/alojamiento-engelberg/disponibilidad',
    fr: '/fr/hebergement-engelberg/disponibilite'
  },
  prices: {
    en: '/engelberg-accommodation/booking/prices',
    de: '/de/engelberg-ferienwohnung/preise',
    es: '/es/alojamiento-engelberg/precios',
    fr: '/fr/hebergement-engelberg/prix'
  },
  process: {
    en: '/engelberg-accommodation/booking/process',
    de: '/de/engelberg-ferienwohnung/buchungsprozess',
    es: '/es/alojamiento-engelberg/proceso-de-reserva',
    fr: '/fr/hebergement-engelberg/processus-de-reservation'
  },
  faq: {
    en: '/engelberg-accommodation/booking/faq',
    de: '/de/engelberg-ferienwohnung/haufige-fragen',
    es: '/es/alojamiento-engelberg/preguntas-frecuentes',
    fr: '/fr/hebergement-engelberg/foire-aux-questions'
  }
};

// Define language-specific paths for summer activities
const summerActivitiesPaths = {
  en: '/engelberg-accommodation/summer-activities',
  de: '/de/engelberg-ferienwohnung/sommer-aktivitaeten',
  es: '/es/alojamiento-engelberg/actividades-de-verano',
  fr: '/fr/hebergement-engelberg/activites-d-ete'
};

// Define language-specific paths for seasonal activities
const seasonalActivitiesPaths = {
  en: '/engelberg-accommodation/seasonal-activities',
  de: '/de/engelberg-ferienwohnung/saisonale-aktivitaeten',
  es: '/es/alojamiento-engelberg/actividades-estacionales',
  fr: '/fr/hebergement-engelberg/activites-saisonnieres'
};

// Define language-specific paths for hiking trails
const hikingTrailsPaths = {
  en: '/en/trail-discovery',
  de: '/de/trail-discovery',
  es: '/es/trail-discovery',
  fr: '/fr/trail-discovery'
};

// Define language-specific paths for German learning platform
const germanLearningPaths = {
  en: '/learn-german',
  de: '/learn-german',
  es: '/learn-german',
  fr: '/learn-german',
  ro: '/learn-german'
};

// Define language-specific paths for family accommodation
const familyAccommodationPaths = {
  en: '/engelberg-accommodation/family-accommodation',
  de: '/de/engelberg-ferienwohnung/familienunterkunft',
  es: '/es/alojamiento-engelberg/alojamiento-familiar',
  fr: '/fr/hebergement-engelberg/hebergement-familial'
};

// Define language-specific paths for chalet section
const chaletPaths = {
  base: {
    en: '/engelberg-accommodation/chalet',
    de: '/de/engelberg-ferienwohnung/chalet',
    es: '/es/alojamiento-engelberg/chalet',
    fr: '/fr/hebergement-engelberg/chalet'
  },
  amenities: {
    en: '/engelberg-accommodation/chalet/amenities',
    de: '/de/engelberg-ferienwohnung/chalet/ausstattung',
    es: '/es/alojamiento-engelberg/chalet/equipamiento',
    fr: '/fr/hebergement-engelberg/chalet/equipements'
  },
  features: {
    en: '/engelberg-accommodation/chalet/features',
    de: '/de/engelberg-ferienwohnung/chalet/merkmale',
    es: '/es/alojamiento-engelberg/chalet/caracteristicas',
    fr: '/fr/hebergement-engelberg/chalet/caracteristiques'
  },
  gallery: {
    en: '/engelberg-accommodation/gallery',
    de: '/de/engelberg-ferienwohnung/galerie',
    es: '/es/alojamiento-engelberg/galeria',
    fr: '/fr/hebergement-engelberg/galerie'
  }
};

// Define language-specific paths for location section
const locationPaths = {
  attractions: {
    en: '/engelberg-accommodation/attractions',
    de: '/de/engelberg-ferienwohnung/sehenswuerdigkeiten',
    es: '/es/alojamiento-engelberg/atracciones',
    fr: '/fr/hebergement-engelberg/attractions'
  },
  transport: {
    en: '/engelberg-accommodation/transport',
    de: '/de/engelberg-ferienwohnung/transport',
    es: '/es/alojamiento-engelberg/transporte',
    fr: '/fr/hebergement-engelberg/transport'
  },
  map: {
    en: '/engelberg-accommodation/map',
    de: '/de/engelberg-ferienwohnung/karte',
    es: '/es/alojamiento-engelberg/mapa',
    fr: '/fr/hebergement-engelberg/carte'
  },
  navigation: {
    en: '/engelberg-accommodation/navigation',
    de: '/de/engelberg-ferienwohnung/navigation',
    es: '/es/alojamiento-engelberg/navegacion',
    fr: '/fr/hebergement-engelberg/navigation'
  }
};

// Helper function to get localized URL based on current language and base path
const getLocalizedHref = (basePath: string, language: string): string => {
  // If it's already an absolute URL (starts with http), return as is
  if (basePath.startsWith('http')) {
    return basePath;
  }

  // Special case for home page - direct to language-specific landing pages
  if (basePath === '/') {
    return homePaths[language as keyof typeof homePaths] || '/';
  }

  // First, normalize the path by removing any existing language prefixes
  let normalizedPath = basePath;

  // Remove English prefix
  if (normalizedPath.startsWith('/engelberg-accommodation')) {
    normalizedPath = normalizedPath.replace('/engelberg-accommodation', '');
  }

  // Remove German prefix
  if (normalizedPath.startsWith('/de/engelberg-ferienwohnung')) {
    normalizedPath = normalizedPath.replace('/de/engelberg-ferienwohnung', '');
  }

  // Remove French prefix
  if (normalizedPath.startsWith('/fr/hebergement-engelberg')) {
    normalizedPath = normalizedPath.replace('/fr/hebergement-engelberg', '');
  }

  // Remove Spanish prefix
  if (normalizedPath.startsWith('/es/alojamiento-engelberg')) {
    normalizedPath = normalizedPath.replace('/es/alojamiento-engelberg', '');
  }

  // Ensure path starts with a slash
  if (!normalizedPath.startsWith('/')) {
    normalizedPath = '/' + normalizedPath;
  }

  // Check if we have a specific mapping for this normalized path

  // Contact section
  if (normalizedPath === '/contact' || normalizedPath.startsWith('/contact/')) {
    const pathType = normalizedPath === '/contact' ? 'base' :
      normalizedPath === '/contact/directions' ? 'directions' :
        normalizedPath === '/contact/inquiry' ? 'inquiry' : '';

    if (pathType && contactPaths[language as keyof typeof contactPaths]?.[pathType as keyof (typeof contactPaths)['en']]) {
      return contactPaths[language as keyof typeof contactPaths][pathType as keyof (typeof contactPaths)['en']];
    }
  }

  // Reviews
  if (normalizedPath === '/reviews') {
    return reviewsPaths[language as keyof typeof reviewsPaths];
  }

  // Sustainable accommodation
  if (normalizedPath === '/sustainable-accommodation') {
    return sustainablePaths[language as keyof typeof sustainablePaths];
  }

  // Booking section
  if (normalizedPath === '/booking' || normalizedPath.startsWith('/booking/')) {
    const pathType = normalizedPath === '/booking' ? 'base' :
      normalizedPath === '/booking/availability' ? 'availability' :
        normalizedPath === '/booking/prices' ? 'prices' :
          normalizedPath === '/booking/process' ? 'process' :
            normalizedPath === '/booking/faq' ? 'faq' : '';

    if (pathType && bookingPaths[pathType as keyof typeof bookingPaths]?.[language as keyof (typeof bookingPaths)['base']]) {
      return bookingPaths[pathType as keyof typeof bookingPaths][language as keyof (typeof bookingPaths)['base']];
    }
  }

  // Summer activities
  if (normalizedPath === '/summer-activities') {
    return summerActivitiesPaths[language as keyof typeof summerActivitiesPaths];
  }

  // Seasonal activities
  if (normalizedPath === '/seasonal-activities') {
    return seasonalActivitiesPaths[language as keyof typeof seasonalActivitiesPaths];
  }

  // German learning platform
  if (normalizedPath === '/learn-german' || normalizedPath.startsWith('/learn-german/')) {
    // For German learning, we use a simple /{lang}/learn-german pattern
    return `/${language}/learn-german`;
  }

  // Hiking trails
  if (normalizedPath === '/trail-discovery') {
    return hikingTrailsPaths[language as keyof typeof hikingTrailsPaths];
  }

  // Family accommodation
  if (normalizedPath === '/family-accommodation') {
    return familyAccommodationPaths[language as keyof typeof familyAccommodationPaths];
  }

  // Chalet section
  if (normalizedPath === '/chalet' || normalizedPath.startsWith('/chalet/') || normalizedPath === '/gallery') {
    const pathType = normalizedPath === '/chalet' ? 'base' :
      normalizedPath === '/chalet/amenities' ? 'amenities' :
        normalizedPath === '/chalet/features' ? 'features' :
          normalizedPath === '/gallery' ? 'gallery' : '';

    if (pathType && chaletPaths[pathType as keyof typeof chaletPaths]?.[language as keyof (typeof chaletPaths)['base']]) {
      return chaletPaths[pathType as keyof typeof chaletPaths][language as keyof (typeof chaletPaths)['base']];
    }
  }

  // Location section
  if (normalizedPath === '/attractions' || normalizedPath === '/transport' ||
    normalizedPath === '/map' || normalizedPath === '/navigation') {
    const pathType = normalizedPath === '/attractions' ? 'attractions' :
      normalizedPath === '/transport' ? 'transport' :
        normalizedPath === '/map' ? 'map' :
          normalizedPath === '/navigation' ? 'navigation' : '';

    if (pathType && locationPaths[pathType as keyof typeof locationPaths]?.[language as keyof (typeof locationPaths)['attractions']]) {
      return locationPaths[pathType as keyof typeof locationPaths][language as keyof (typeof locationPaths)['attractions']];
    }
  }

  // If no specific mapping was found, generate a language-specific URL
  // Use language prefix for the language and append the normalized path
  switch (language) {
    case 'de':
      return `/de/engelberg-ferienwohnung${normalizedPath}`;
    case 'fr':
      return `/fr/hebergement-engelberg${normalizedPath}`;
    case 'es':
      return `/es/alojamiento-engelberg${normalizedPath}`;
    default: // 'en'
      return `/engelberg-accommodation${normalizedPath}`;
  }
};

interface NavItemProps {
  href: string;
  children: React.ReactNode;
  onClick?: () => void;
}

// Memoize NavItem to prevent unnecessary re-renders
const NavItem = React.memo<NavItemProps>(({ href, children, onClick }) => {
  const { language } = useLanguage();

  // Use the getLocalizedHref function with special case for home page
  let localizedHref = getLocalizedHref(href, language);

  return (
    <Link
      href={localizedHref}
      className="px-3 py-2 rounded-md text-sm font-medium transition-all duration-150 text-gray-300 hover:bg-alpine-blue-700 hover:text-chalet-gold-400 active:scale-95 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-chalet-gold-400/50 relative after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-0 after:bg-chalet-gold-400 hover:after:w-full after:transition-all after:duration-300 hover:translate-y-[-2px]"
      onClick={onClick}
    >
      {children}
    </Link>
  );
});

NavItem.displayName = "NavItem";

interface DropdownItemProps {
  href: string;
  children: React.ReactNode;
  onClick: () => void;
  index: number;
}

// Memoize DropdownItem to prevent unnecessary re-renders
const DropdownItem = React.memo<DropdownItemProps>(({ href, children, onClick, index }) => {
  const { language } = useLanguage();
  const localizedHref = getLocalizedHref(href, language);

  return (
    <Link
      href={localizedHref}
      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-alpine-blue-800 hover:pl-6 transition-all duration-200 opacity-0 animate-fadeIn font-medium bg-white"
      onClick={onClick}
      style={{
        animationDelay: `${30 * index}ms`,
        animationFillMode: 'forwards'
      }}
    >
      {children}
    </Link>
  );
});

DropdownItem.displayName = "DropdownItem";

interface DropdownProps {
  id: string;
  label: string;
  items: Array<{ href: string; label: string }>;
  activeDropdown: string | null;
  setActiveDropdown: (id: string | null) => void;
  closeMobileMenu?: () => void;
}

// Memoize Dropdown component
const Dropdown = React.memo<DropdownProps>(({
  id,
  label,
  items,
  activeDropdown,
  setActiveDropdown,
  closeMobileMenu
}) => {
  const isOpen = activeDropdown === id;
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownContentRef = useRef<HTMLDivElement>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Memoize the mouse over check function
  const isMouseOverDropdown = useCallback((event: MouseEvent): boolean => {
    if (!dropdownRef.current || !dropdownContentRef.current) return false;

    const dropdownRect = dropdownRef.current.getBoundingClientRect();
    const contentRect = dropdownContentRef.current.getBoundingClientRect();

    // Check if mouse is over dropdown button
    if (
      event.clientX >= dropdownRect.left &&
      event.clientX <= dropdownRect.right &&
      event.clientY >= dropdownRect.top &&
      event.clientY <= dropdownRect.bottom
    ) {
      return true;
    }

    // Check if mouse is over dropdown content
    if (
      event.clientX >= contentRect.left &&
      event.clientX <= contentRect.right &&
      event.clientY >= contentRect.top &&
      event.clientY <= contentRect.bottom
    ) {
      return true;
    }

    return false;
  }, []);

  const handleToggle = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (isOpen) {
      setActiveDropdown(null);
    } else {
      setActiveDropdown(id);
    }
  }, [isOpen, setActiveDropdown, id]);

  const handleMouseEnter = useCallback(() => {
    // Clear any pending close timeouts
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setActiveDropdown(id);
  }, [id, setActiveDropdown]);

  const handleMouseLeave = useCallback(() => {
    // Start a document-wide mouse tracking when mouse leaves the dropdown
    const trackMousePosition = (event: MouseEvent) => {
      if (isMouseOverDropdown(event)) {
        // Mouse is over dropdown or its content, cancel timeout
        if (closeTimeoutRef.current) {
          clearTimeout(closeTimeoutRef.current);
          closeTimeoutRef.current = null;
        }
      } else {
        // Mouse is outside all dropdown elements, close almost immediately
        // Using minimal 50ms timeout just to prevent edge cases
        if (!closeTimeoutRef.current) {
          closeTimeoutRef.current = setTimeout(() => {
            setActiveDropdown(null);
            document.removeEventListener('mousemove', trackMousePosition);
            closeTimeoutRef.current = null;
          }, 50); // Reduced from 100ms to 50ms for faster response
        }
      }
    };

    // Add document-wide mouse tracking
    document.addEventListener('mousemove', trackMousePosition);

    // Clean up listener when dropdown closes or component unmounts
    if (activeDropdown !== id) {
      document.removeEventListener('mousemove', trackMousePosition);
    }
  }, [isMouseOverDropdown, activeDropdown, id, setActiveDropdown]);

  const handleItemClick = useCallback(() => {
    setActiveDropdown(null);
    if (closeMobileMenu) closeMobileMenu();
  }, [setActiveDropdown, closeMobileMenu]);

  // Handle scroll events to keep dropdown open while scrolling
  useEffect(() => {
    const handleScroll = () => {
      // If dropdown is open, prevent it from closing due to scroll
      if (isOpen && closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
    };

    // Use passive: true for better performance
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      // Clean up any remaining timeouts
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, [isOpen]);

  return (
    <div
      className="relative group dropdown-container"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      ref={dropdownRef}
    >
      <button
        className={`px-3 py-2 rounded-md text-sm font-medium flex items-center space-x-1 transition-all duration-150 ease-in-out text-gray-300 hover:bg-alpine-blue-700 hover:text-chalet-gold-400 focus:outline-none focus:ring-2 focus:ring-chalet-gold-400/50 ${isOpen ? 'bg-alpine-blue-700 text-chalet-gold-400 shadow-lg' : ''}`}
        onClick={handleToggle}
        aria-expanded={isOpen}
      >
        <span className="relative">
          {label}
          <span className={`absolute -bottom-1 left-0 w-0 h-0.5 bg-chalet-gold-400 transition-all duration-150 ${isOpen ? 'w-full' : ''}`}></span>
        </span>
        <ChevronDownIcon className={`h-4 w-4 transition-transform duration-300 ${isOpen ? 'rotate-180 text-chalet-gold-400' : ''}`} />
      </button>
      <div
        ref={dropdownContentRef}
        className={`absolute left-0 mt-2 w-52 rounded-md shadow-xl py-1 bg-white ring-1 ring-black ring-opacity-5 z-50 transform origin-top-left transition-all duration-150 overflow-hidden ${closeMobileMenu ? 'bg-white' : 'bg-white'} ${isOpen
          ? 'opacity-100 max-h-[600px] scale-100 translate-y-0'
          : 'opacity-0 max-h-0 scale-95 -translate-y-2 pointer-events-none'
          }`}
        style={{
          zIndex: closeMobileMenu ? 70 : 50,
          position: closeMobileMenu ? 'fixed' : 'absolute',
          left: closeMobileMenu ? 'auto' : '0',
          right: closeMobileMenu ? '1rem' : 'auto',
          top: closeMobileMenu ? '0' : '100%',
          width: closeMobileMenu ? 'calc(60% - 1rem)' : '13rem',
          maxHeight: closeMobileMenu ? '80vh' : '600px',
          marginTop: closeMobileMenu ? '0' : '0.5rem'
        }}
      >
        {items.map((item, index) => (
          <DropdownItem
            key={index}
            href={item.href}
            onClick={handleItemClick}
            index={index}
          >
            {item.label}
          </DropdownItem>
        ))}
      </div>
    </div>
  );
});

Dropdown.displayName = "Dropdown";

const Navigation: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [logoUrl, setLogoUrl] = useState<string>(DEFAULT_BRANDING.logoUrl);
  const [menuVisibility, setMenuVisibility] = useState<Record<string, Record<string, boolean>>>({});
  const { language, translate } = useLanguage();
  const { user, isAdmin } = useAuth();

  // Load branding configuration to get the logo URL and menu visibility settings
  useEffect(() => {
    const loadBranding = async () => {
      try {
        const brandingConfig = await getBrandingConfig();
        setLogoUrl(brandingConfig.logoUrl);

        // Load menu visibility settings
        if (brandingConfig.menuConfiguration?.submenuItemsVisibility) {
          console.log('Loaded menu visibility:', brandingConfig.menuConfiguration.submenuItemsVisibility);
          setMenuVisibility(brandingConfig.menuConfiguration.submenuItemsVisibility);
        }
      } catch (error) {
        console.error('Error loading branding configuration:', error);
        // Use default logo if loading fails
        setLogoUrl(DEFAULT_BRANDING.logoUrl);
      }
    };

    loadBranding();

    // Add event listener for refresh
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'branding_updated') {
        console.log('Branding updated, reloading menu visibility');
        loadBranding();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Helper function to check if a submenu item should be visible
  const isSubmenuItemVisible = useCallback((section: string, item: string): boolean => {
    // Admins can always see all menu items
    if (isAdmin) return true;

    // If no visibility settings, default to visible
    if (!menuVisibility[section]) return true;

    // Check specific item visibility
    return menuVisibility[section][item] !== false;
  }, [menuVisibility, isAdmin]);

  // Close dropdown when clicked outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (activeDropdown && !(event.target as Element).closest('.dropdown-container')) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeDropdown]);

  // Close mobile menu on page navigation
  React.useEffect(() => {
    const handleRouteChange = () => {
      setIsMobileMenuOpen(false);
      setActiveDropdown(null);
    };

    window.addEventListener('popstate', handleRouteChange);
    return () => window.removeEventListener('popstate', handleRouteChange);
  }, []);

  // Auto collapse mobile menu after 10 seconds of inactivity
  const mobileMenuTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const resetMobileMenuTimeout = useCallback(() => {
    if (mobileMenuTimeoutRef.current) {
      clearTimeout(mobileMenuTimeoutRef.current);
    }

    if (isMobileMenuOpen) {
      mobileMenuTimeoutRef.current = setTimeout(() => {
        setIsMobileMenuOpen(false);
      }, 10000); // 10 seconds
    }
  }, [isMobileMenuOpen]);

  // Set up the timeout when mobile menu opens
  useEffect(() => {
    if (isMobileMenuOpen) {
      resetMobileMenuTimeout();
    } else if (mobileMenuTimeoutRef.current) {
      clearTimeout(mobileMenuTimeoutRef.current);
    }

    return () => {
      if (mobileMenuTimeoutRef.current) {
        clearTimeout(mobileMenuTimeoutRef.current);
      }
    };
  }, [isMobileMenuOpen, resetMobileMenuTimeout]);

  // Reset timeout on user interaction with mobile menu
  const handleMobileMenuInteraction = useCallback(() => {
    resetMobileMenuTimeout();
  }, [resetMobileMenuTimeout]);

  // 1. Keep your existing toggle function
  const toggleMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  }, [isMobileMenuOpen]);

  // 2. Add a ref for the mobile menu
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  // Close mobile menu
  const closeMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(false);
  }, []);


  // Define navigation with translation keys
  const navItems = {
    home: {
      key: 'nav.home',
      default: 'Home',
      href: '/'
    },
    property: {
      key: 'nav.property',
      default: 'Chalet',
      dropdown: [
        { key: 'nav.property.overview', default: 'Explore the Chalet', href: '/engelberg-accommodation/chalet' },
        { key: 'nav.property.amenities', default: 'Amenities', href: '/engelberg-accommodation/chalet/amenities' },
        { key: 'nav.property.features', default: 'Features', href: '/engelberg-accommodation/chalet/features' },
        { key: 'nav.property.gallery', default: 'Photo Gallery', href: '/engelberg-accommodation/gallery' },
        { key: 'nav.property.virtual', default: '360° Tour', href: 'https://my.matterport.com/show/?m=FpvN3DrCoa7' },
        { key: 'nav.property.sustainable', default: 'Sustainable Accommodation', href: '/engelberg-accommodation/sustainable-accommodation' },
        { key: 'nav.property.engelbergPhotos', default: 'Photos from Engelberg', href: 'https://explore.pavelmaxim.ch/en/photos-from-engelberg/' },
      ]
    },
    location: {
      key: 'nav.location',
      default: 'Location',
      dropdown: [
        { key: 'nav.location.attractions', default: 'Local Attractions', href: '/engelberg-accommodation/attractions' },
        { key: 'nav.location.transport', default: 'Transport', href: '/engelberg-accommodation/transport' },
        { key: 'nav.location.map', default: 'Interactive Map', href: '/engelberg-accommodation/map' },
        { key: 'nav.location.gps', default: 'Find Your Way (GPS)', href: '/engelberg-accommodation/navigation' },
      ]
    },
    activities: {
      key: 'nav.activities',
      default: 'Activities',
      dropdown: [
        { key: 'nav.activities.skiing', default: 'Skiing & Snowboarding', href: '/engelberg-accommodation/ski-chalet' },
        { key: 'nav.activities.summer', default: 'Summer Activities', href: '/engelberg-accommodation/summer-activities' },
        { key: 'nav.activities.family', default: 'Family Adventures', href: '/engelberg-accommodation/family-accommodation' },
        { key: 'nav.activities.seasonal', default: 'Seasonal Events', href: '/engelberg-accommodation/seasonal-activities' },
        { key: 'nav.activities.hiking', default: 'Hiking Trails', href: '/engelberg-accommodation/trail-discovery' },
        { key: 'nav.activities.weatherteller', default: 'Weather', href: 'https://app.weatherteller.com/' },
      ]
    },
    booking: {
      key: 'nav.booking',
      default: 'Booking',
      dropdown: [
        { key: 'nav.booking.availability', default: 'Check Availability', href: '/engelberg-accommodation/booking/availability' },
        { key: 'nav.booking.prices', default: 'Prices & Offers', href: '/engelberg-accommodation/booking/prices' },
        { key: 'nav.booking.process', default: 'Booking Process', href: '/engelberg-accommodation/booking/process' },
        { key: 'nav.booking.faq', default: 'FAQs', href: '/engelberg-accommodation/booking/faq' },
      ]
    },
    engelTalk: {
      key: 'nav.engelTalk',
      default: '🏔️🗣️ EngelTalk',
      href: '/learn-german'
    },
    contact: {
      key: 'nav.contact',
      default: 'Contact',
      dropdown: [
        { key: 'nav.contact.info', default: 'Contact Us', href: '/engelberg-accommodation/contact' },
        { key: 'nav.contact.directions', default: 'Directions', href: '/engelberg-accommodation/contact/directions' },
        { key: 'nav.contact.inquiry', default: 'Inquiry Form', href: '/engelberg-accommodation/contact/inquiry' },
        { key: 'nav.reviews', default: 'Reviews', href: '/engelberg-accommodation/reviews' },
      ]
    }
  };

  // Get translated label for a menu item - memoized to prevent unnecessary recalculations
  const getTranslatedItem = useCallback((key: string, defaultValue: string): string => {
    return translate(key, defaultValue);
  }, [translate]);

  // Prepare dropdown items with translations - memoized
  const getTranslatedDropdownItems = useCallback((items: Array<{ key: string; default: string; href: string }>) => {
    return items.map(item => ({
      href: item.href,
      label: getTranslatedItem(item.key, item.default)
    }));
  }, [getTranslatedItem]);

  // Filter menu items based on visibility settings and translate them
  const getFilteredAndTranslatedItems = useCallback((
    items: Array<{ key: string; default: string; href: string }>,
    sectionKey: string
  ) => {
    // Map of predefined submenu keys for each section based on BrandingService configuration
    const submenuKeyMappings: Record<string, Record<string, string>> = {
      property: {
        '/chalet': 'overview',
        '/chalet/amenities': 'amenities',
        '/chalet/ausstattung': 'amenities',
        '/chalet/equipamiento': 'amenities',
        '/chalet/equipements': 'amenities',
        '/chalet/features': 'features',
        '/chalet/merkmale': 'features',
        '/chalet/caracteristicas': 'features',
        '/chalet/caracteristiques': 'features',
        '/gallery': 'gallery',
        '/galerie': 'gallery',
        '/galeria': 'gallery'
      },
      location: {
        '/engelberg-accommodation': 'engelberg',
        '/attractions': 'attractions',
        '/sehenswuerdigkeiten': 'attractions',
        '/atracciones': 'attractions',
        '/transport': 'transport',
        '/transporte': 'transport',
        '/map': 'map',
        '/karte': 'map',
        '/mapa': 'map',
        '/carte': 'map',
        '/navigation': 'gps',
        '/navegacion': 'gps'
      },
      activities: {
        '/ski-chalet': 'skiing',
        '/family-accommodation': 'family',
        '/familienunterkunft': 'family',
        '/alojamiento-familiar': 'family',
        '/hebergement-familial': 'family',
        '/seasonal-activities': 'seasonal',
        '/saisonale-aktivitaeten': 'seasonal',
        '/actividades-estacionales': 'seasonal',
        '/activites-saisonnieres': 'seasonal',
        '/sommer-aktivitaeten': 'summer',
        '/summer-activities': 'summer',
        '/actividades-de-verano': 'summer',
        '/activites-d-ete': 'summer'
      },
      booking: {
        '/booking/availability': 'availability',
        '/booking/prices': 'prices',
        '/booking/process': 'process',
        '/booking/faq': 'faq'
      },
      contact: {
        '/contact': 'info',
        '/kontakt': 'info',
        '/contacto': 'info',
        '/contact/directions': 'directions',
        '/kontakt/anfahrt': 'directions',
        '/contacto/como-llegar': 'directions',
        '/contact/itineraire': 'directions',
        '/contact/inquiry': 'inquiry',
        '/kontakt/anfrage': 'inquiry',
        '/contacto/consulta': 'inquiry',
        '/contact/demande': 'inquiry'
      }
    };

    return items
      .filter(item => {
        // Use explicit mapping instead of extracting from URL
        const submenuKey = submenuKeyMappings[sectionKey]?.[item.href] || item.href.split('/').pop() || '';
        return isSubmenuItemVisible(sectionKey, submenuKey);
      })
      .map(item => ({
        href: item.href,
        label: getTranslatedItem(item.key, item.default)
      }));
  }, [getTranslatedItem, isSubmenuItemVisible]);

  // Pre-calculate translated dropdown items to avoid recalculating on every render
  const translatedDropdowns = useMemo(() => {
    return {
      property: getFilteredAndTranslatedItems(navItems.property.dropdown, 'property'),
      location: getFilteredAndTranslatedItems(navItems.location.dropdown, 'location'),
      activities: getFilteredAndTranslatedItems(navItems.activities.dropdown, 'activities'),
      booking: getFilteredAndTranslatedItems(navItems.booking.dropdown, 'booking'),
      contact: getFilteredAndTranslatedItems(navItems.contact.dropdown, 'contact')
    };
  }, [getFilteredAndTranslatedItems, language]);

  // Add useMemo to expensive computations:
  const dropdownStyle = useMemo(() => ({
    zIndex: typeof closeMobileMenu === 'function' ? 70 : 50,
    position: typeof closeMobileMenu === 'function' ? 'fixed' : 'absolute',
    left: typeof closeMobileMenu === 'function' ? '1rem' : '0',
    width: typeof closeMobileMenu === 'function' ? 'calc(100% - 2rem)' : '13rem'
  }), [closeMobileMenu]);



  return (
    <nav className="bg-alpine-blue-800 text-white sticky top-0 z-50 shadow-md bg-opacity-100">      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex items-center justify-between h-16">
        <div className="flex items-center">
          <Link className="flex items-center" href="/">
            <div className="relative h-16 w-16 overflow-hidden rounded-full border-2 border-chalet-gold-400/60 shadow-lg transform transition-transform duration-300 hover:scale-105 mr-3">
              <Image
                alt="Tannenboden Chalet Engelberg"
                width={64}
                height={64}
                className="object-cover w-full h-full"
                src={logoUrl}
                priority
              />
            </div>
            <span className="font-serif text-chalet-gold-400 text-xl font-bold hover:text-white transition-colors duration-300">
              {translate('site.name', 'Tannenboden Chalet Engelberg')}
            </span>
          </Link>
        </div>
        <div className="hidden md:block">
          <div className="ml-10 flex items-center space-x-4">
            <NavItem href={navItems.home.href}>
              {getTranslatedItem(navItems.home.key, navItems.home.default)}
            </NavItem>
            <Dropdown
              id="property-dropdown"
              label={getTranslatedItem(navItems.property.key, navItems.property.default)}
              items={translatedDropdowns.property}
              activeDropdown={activeDropdown}
              setActiveDropdown={setActiveDropdown}
            />
            <Dropdown
              id="location-dropdown"
              label={getTranslatedItem(navItems.location.key, navItems.location.default)}
              items={translatedDropdowns.location}
              activeDropdown={activeDropdown}
              setActiveDropdown={setActiveDropdown}
            />
            <Dropdown
              id="activities-dropdown"
              label={getTranslatedItem(navItems.activities.key, navItems.activities.default)}
              items={translatedDropdowns.activities}
              activeDropdown={activeDropdown}
              setActiveDropdown={setActiveDropdown}
            />
            <Dropdown
              id="booking-dropdown"
              label={getTranslatedItem(navItems.booking.key, navItems.booking.default)}
              items={translatedDropdowns.booking}
              activeDropdown={activeDropdown}
              setActiveDropdown={setActiveDropdown}
            />
            <NavItem href={navItems.engelTalk.href}>
              {getTranslatedItem(navItems.engelTalk.key, navItems.engelTalk.default)}
            </NavItem>
            <Dropdown
              id="contact-dropdown"
              label={getTranslatedItem(navItems.contact.key, navItems.contact.default)}
              items={translatedDropdowns.contact}
              activeDropdown={activeDropdown}
              setActiveDropdown={setActiveDropdown}
            />
            {isAdmin && (
              <NavItem href="/admin">
                {translate('nav.admin', 'Administrator')}
              </NavItem>
            )}
          </div>
        </div>

        {/* Desktop Language Selector */}
        <div className="hidden md:flex items-center space-x-4">
          <LanguageSelector />
        </div>

        <div className="md:hidden">
          <button
            className="bg-alpine-blue-700 inline-flex items-center justify-center p-2 rounded-md text-white hover:text-chalet-gold-400 hover:bg-alpine-blue-800 focus:outline-none border border-chalet-gold-400/20 hover:border-chalet-gold-400 transition-all duration-150 shadow-sm hover:shadow active:scale-95"
            onClick={toggleMobileMenu}
            aria-expanded={isMobileMenuOpen}
          >
            <span className="sr-only">Open main menu</span>
            <MenuIcon className="h-6 w-6" />
          </button>
        </div>
      </div>
    </div>
      {/* Mobile menu with transition animation */}
      <div
        ref={mobileMenuRef}
        className={`md:hidden bg-alpine-blue-800 border-t border-chalet-gold-400/30 overflow-hidden transition-all duration-150 ease-out ${isMobileMenuOpen
          ? 'max-h-[800px] opacity-100 translate-y-0'
          : 'max-h-0 opacity-0 -translate-y-2 pointer-events-none'
          }`}
        onMouseMove={handleMobileMenuInteraction}
        onTouchStart={handleMobileMenuInteraction}
        onClick={handleMobileMenuInteraction}
        style={{
          position: 'relative',
          zIndex: 40,
          willChange: 'transform, opacity',
          transform: 'translateZ(0)'
        }}
      >
        <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
          <NavItem
            href={navItems.home.href}
            onClick={closeMobileMenu}
          >
            {getTranslatedItem(navItems.home.key, navItems.home.default)}
          </NavItem>
          <Dropdown
            id="mobile-property-dropdown"
            label={getTranslatedItem(navItems.property.key, navItems.property.default)}
            items={translatedDropdowns.property}
            activeDropdown={activeDropdown}
            setActiveDropdown={setActiveDropdown}
            closeMobileMenu={() => setIsMobileMenuOpen(false)}
          />
          <Dropdown
            id="mobile-location-dropdown"
            label={getTranslatedItem(navItems.location.key, navItems.location.default)}
            items={translatedDropdowns.location}
            activeDropdown={activeDropdown}
            setActiveDropdown={setActiveDropdown}
            closeMobileMenu={() => setIsMobileMenuOpen(false)}
          />
          <Dropdown
            id="mobile-activities-dropdown"
            label={getTranslatedItem(navItems.activities.key, navItems.activities.default)}
            items={translatedDropdowns.activities}
            activeDropdown={activeDropdown}
            setActiveDropdown={setActiveDropdown}
            closeMobileMenu={() => setIsMobileMenuOpen(false)}
          />
          <Dropdown
            id="mobile-booking-dropdown"
            label={getTranslatedItem(navItems.booking.key, navItems.booking.default)}
            items={translatedDropdowns.booking}
            activeDropdown={activeDropdown}
            setActiveDropdown={setActiveDropdown}
            closeMobileMenu={() => setIsMobileMenuOpen(false)}
          />
          <NavItem
            href={navItems.engelTalk.href}
            onClick={closeMobileMenu}
          >
            {getTranslatedItem(navItems.engelTalk.key, navItems.engelTalk.default)}
          </NavItem>
          <Dropdown
            id="mobile-contact-dropdown"
            label={getTranslatedItem(navItems.contact.key, navItems.contact.default)}
            items={translatedDropdowns.contact}
            activeDropdown={activeDropdown}
            setActiveDropdown={setActiveDropdown}
            closeMobileMenu={() => setIsMobileMenuOpen(false)}
          />


          {isAdmin && (
            <NavItem
              href="/admin"
              onClick={closeMobileMenu}
            >
              {translate('nav.admin', 'Administrator')}
            </NavItem>
          )}
        </div>
        {/* Mobile Language Selector */}
        <div className="mt-2 pt-2 border-t border-chalet-gold-400/20 px-3 py-2 animate-fadeIn" style={{ animationDelay: '150ms' }}>
          <span className="text-sm text-gray-400 block mb-2">
            {translate('language.select', 'Selectați limba')}
          </span>
          <LanguageSelector />
        </div>
      </div>
    </nav>
  );
};

// Export memoized component to prevent unnecessary rerenders
export default React.memo(Navigation);